import React, { useState, useRef } from 'react'
import {
  Form,
  ButtonToolbar,
  Button,
  FormInstance,
  Text,
  PasswordInput,
} from 'rsuite'

import FormField from 'shared/FormField/FormField'
import { PhoneInput } from 'shared/PhoneInput/PhoneInput'
import PasswordRequirements from 'shared/PasswordRequirements/PasswordRequirements'

import { getSchemaModel } from './formSchema'

export type CreateEditUserFormValue = {
  name: string
  username: string
  email: string
  password: string
  passwordConfirmation?: string
  phone: string
}

type FormMode = 'create' | 'edit'

type Props = {
  okCallback: (value: CreateEditUserFormValue) => void
  cancelCallback?: () => void
  requirePasswordConfirmation?: boolean
  createButtonText?: string
  initialFormValue?: CreateEditUserFormValue
  mode?: FormMode
  additionalFields?: React.ReactNode
  additionalSubmitDisabled?: boolean
}

// The rsuite form validation library uses another library under the hood called
// "schema-typed". When using it in the prescribed manner, it introduces side
// effects that do things like cause the little red error tags to show up.
// By default, even if a required field is empty, the error tag won't show until
// you try to submit the form.
// However, from a UX standpoint I want to disable the submit button entirely if
// we know the form won't pass validation because required fields are empty, so
// we need to do that check manually.
// Things get spicier when you consider that the password confirmation field is
// conditionally required based on a prop, so we need to account for that in our
// validation logic as well. Hence the need to track form errors in state and do
// some manual validation checks in addition to using the schema-typed library
// for its intended purpose of showing error tags and messages on the form
// fields.

const requiredFieldsPresent = (
  formValue: CreateEditUserFormValue,
  mode: FormMode,
) =>
  [
    'name',
    'username',
    'email',
    ...(mode === 'create' ? ['password'] : []),
  ].every((field) => formValue[field as keyof CreateEditUserFormValue])

const defaultInitialFormValue: CreateEditUserFormValue = {
  name: '',
  username: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  phone: '',
}

const CreateEditUserForm: React.FC<Props> = (props): React.ReactElement => {
  const {
    okCallback,
    cancelCallback,
    requirePasswordConfirmation,
    createButtonText = 'Create User',
    initialFormValue = defaultInitialFormValue,
    mode = 'create',
    additionalFields,
    additionalSubmitDisabled = false,
  } = props

  const [message, setMessage] = useState('')

  const [formValue, setFormValue] = useState<CreateEditUserFormValue>({
    ...initialFormValue,
  })
  const [formError, setFormError] = useState({})

  const model = getSchemaModel(requirePasswordConfirmation, mode === 'edit')

  const formRef = useRef<FormInstance | null>(null)

  const messageColor = message.toLowerCase().includes('error') ? 'red' : 'green'

  const formValid = Object.keys(formError || {}).length === 0
  const submitDisabled =
    !formValid ||
    !requiredFieldsPresent(formValue, mode) ||
    additionalSubmitDisabled ||
    (requirePasswordConfirmation && !formValue.passwordConfirmation)

  const onSubmit = async () => {
    const isFormValid = await formRef.current?.checkAsync()

    if (!isFormValid) {
      setMessage('There are errors in your submission.')
      return
    }

    try {
      await okCallback(formValue)
      if (mode === 'create') {
        setFormValue({ ...defaultInitialFormValue })
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      const action = mode === 'edit' ? 'updating user' : 'creating user'
      console.error(`Error ${action}: ${errorMessage}`)
      setMessage(`Error ${action}: ${errorMessage}`)
    }
  }

  return (
    <>
      <Form
        fluid
        model={model}
        ref={formRef}
        formValue={formValue}
        onChange={(value) => setFormValue(value as CreateEditUserFormValue)}
        onCheck={setFormError}
        onSubmit={onSubmit}
        autoComplete="off"
      >
        <FormField name="name" label="Name" text="Name is required." />
        <FormField name="email" label="Email" text="Email is required." />
        <FormField
          name="username"
          label="Username"
          text="Username is required and must be unique. Minimum 6 characters, maximum 30 characters."
          autoComplete="new-password"
          checkAsync={mode === 'create'}
          disabled={mode === 'edit'}
        />
        <FormField
          name="password"
          label="Password"
          text={
            mode === 'edit'
              ? 'Leave blank to keep the current password. New passwords must be at least 8 characters and include uppercase, lowercase, number, and special character.'
              : 'Password is required. Must be at least 8 characters and include uppercase, lowercase, number, and special character.'
          }
          autoComplete="new-password"
          accepter={PasswordInput}
        />
        {requirePasswordConfirmation && (
          <FormField
            name="passwordConfirmation"
            label="Confirm Password"
            text="Please confirm your password."
            accepter={PasswordInput}
          />
        )}
        <PasswordRequirements
          password={formValue.password}
          confirmPassword={formValue.passwordConfirmation ?? ''}
          requireConfirmation={!!requirePasswordConfirmation}
        />
        <FormField
          name="phone"
          label="Phone Number"
          accepter={PhoneInput}
          text="Phone number is optional."
        />
        {additionalFields}

        <Form.Group>
          <ButtonToolbar>
            {cancelCallback && <Button onClick={cancelCallback}>Cancel</Button>}
            <Button
              appearance="primary"
              type="submit"
              disabled={submitDisabled}
              block={!cancelCallback}
            >
              {createButtonText}
            </Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>
      {message && <Text color={messageColor}>{message}</Text>}
    </>
  )
}

export default CreateEditUserForm
